import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  UploadCloud, Wand2, Plus, Bot, RefreshCw, Clock, Check, AlertTriangle, Activity,
  Briefcase, Users, UserCog, Database, ChevronRight, Search as SearchIcon, ShieldCheck,
  Globe, Eye, Mail, ExternalLink, Sparkles, Cpu, Image as ImageIcon, FileText, Zap,
  ClipboardList, Bug, CheckCircle2, Trash2, Building2, Megaphone, ScrollText,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";

function StatPill({ label, value, accent }) {
  return (
    <div className="vital-card-static p-4">
      <div className="eyebrow">{label}</div>
      <div className={`text-2xl font-black tracking-tight mt-1 ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

// ---------------- Master Directory: Companies discovery widget ----------------
function CompaniesDiscoveryWidget() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/directory/companies/discovery-status");
      setStats(r.data);
    } catch (e) { console.error("discovery-status load failed:", e); /* widget is optional */ }
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const runBatch = async (opts = {}) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/discover-careers-urls", null, { params: { batch: 100, ...opts } });
      toast.success(`Discovery batch queued — ${r.data.queued} companies. Check back in ~2 min.`);
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start discovery");
    } finally { setBusy(false); }
  };

  if (!stats) return null;
  const covered = (stats.found || 0) + Math.max(0, (stats.with_live_jobs || 0) - (stats.found || 0));
  const coveragePct = stats.total ? Math.round((covered / stats.total) * 100) : 0;

  return (
    <div className="mb-4 rounded-lg border border-border bg-muted/20 p-3" data-testid="companies-discovery-widget">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <div><span className="text-muted-foreground uppercase tracking-wider">Discovery status</span></div>
          <div className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="font-semibold tabular-nums">{stats.found.toLocaleString()}</span><span className="text-muted-foreground">found</span></div>
          <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-amber-600" /><span className="font-semibold tabular-nums">{stats.pending.toLocaleString()}</span><span className="text-muted-foreground">pending</span></div>
          <div className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-rose-500" /><span className="font-semibold tabular-nums">{stats.not_found.toLocaleString()}</span><span className="text-muted-foreground">not found</span></div>
          <div className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-blue-600" /><span className="font-semibold tabular-nums">{stats.with_live_jobs.toLocaleString()}</span><span className="text-muted-foreground">with live jobs</span></div>
          <div className="pl-3 border-l border-border">
            <span className="font-semibold">{coveragePct}%</span> <span className="text-muted-foreground">directory coverage</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" disabled={busy || stats.pending === 0} onClick={() => runBatch()} data-testid="discovery-run-pending">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
            Discover 100 pending
          </Button>
          <Button size="sm" variant="outline" disabled={busy || stats.not_found === 0} onClick={() => runBatch({ include_not_found: true })} title="Re-probe previously abandoned companies with the improved detector" data-testid="discovery-reprobe-notfound">
            <Bug className="h-3.5 w-3.5 mr-1" />
            Re-probe not-found
          </Button>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${coveragePct}%` }} />
      </div>
    </div>
  );
}

// ---------------- Add / Edit Company modal ----------------
function CompanyFormDialog({ initial, onCancel, onSave }) {
  // `initial = {}` means "new company"; otherwise all fields prefilled from the row.
  const [form, setForm] = useState({
    company_id: initial?.company_id || null,
    name: initial?.name || "",
    sector: initial?.sector || "",
    careers_url: initial?.careers_url || "",
    parent_country: initial?.parent_country || "",
    cities: (initial?.cities || []).join(", "),
    domain: initial?.domain || "",
    ats: initial?.ats || "",
    india_headcount: initial?.india_headcount ?? "",
    crawlable: initial?.crawlable !== false,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.company_id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="company-form-dialog"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-lg border border-border bg-background shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold">{isEdit ? `Edit — ${initial.name}` : "Add company"}</h3>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground text-lg leading-none" data-testid="company-form-close">×</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Company name *</span>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="company-form-name" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Sector</span>
            <Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="BFSI · Tech · Pharma · …" data-testid="company-form-sector" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Careers URL</span>
            <Input value={form.careers_url} onChange={(e) => setForm({ ...form, careers_url: e.target.value })} placeholder="https://careers.example.com" data-testid="company-form-careers-url" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Cities (comma-sep)</span>
              <Input value={form.cities} onChange={(e) => setForm({ ...form, cities: e.target.value })} placeholder="Bengaluru, Mumbai" data-testid="company-form-cities" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Parent country</span>
              <Input value={form.parent_country} onChange={(e) => setForm({ ...form, parent_country: e.target.value })} placeholder="USA · UK · Japan …" data-testid="company-form-country" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Domain</span>
              <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="example.com" data-testid="company-form-domain" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Crawler kind (ATS)</span>
              <Input value={form.ats} onChange={(e) => setForm({ ...form, ats: e.target.value })} placeholder="workday · icims · greenhouse · …" data-testid="company-form-ats" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">India headcount</span>
              <Input type="number" value={form.india_headcount} onChange={(e) => setForm({ ...form, india_headcount: e.target.value })} data-testid="company-form-headcount" />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.crawlable} onChange={(e) => setForm({ ...form, crawlable: e.target.checked })} data-testid="company-form-crawlable" />
              <span className="text-sm">Include in nightly crawl</span>
            </label>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-muted/20">
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving} data-testid="company-form-cancel">Cancel</Button>
          <Button type="submit" disabled={saving || !form.name.trim()} data-testid="company-form-save">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add company")}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Master Directory tab ----------------
function MasterDirectory() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState({ items: [], total: 0 });
  const [seekers, setSeekers] = useState({ items: [], total: 0 });
  const [recruiters, setRecruiters] = useState({ items: [], total: 0 });
  const [companies, setCompanies] = useState({ items: [], total: 0, page: 0 });
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [companiesWithJobs, setCompaniesWithJobs] = useState(null); // null=all, true=has-jobs, false=zero-jobs
  const [page, setPage] = useState(0);
  const [companiesPage, setCompaniesPage] = useState(0);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [editing, setEditing] = useState(null);        // company doc being edited, or {} for new, or null when closed
  const [busyIds, setBusyIds] = useState(new Set());   // per-row spinner
  const PAGE_SIZE = 100;

  useEffect(() => { setPage(0); }, [q, onlyActive, tab]);
  useEffect(() => { setCompaniesPage(0); setSelectedCompanyIds(new Set()); }, [q, companiesWithJobs]);

  const toggleCompanySelect = (cid) => {
    setSelectedCompanyIds(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };
  const toggleSelectAllVisible = (checked) => {
    setSelectedCompanyIds(prev => {
      if (!checked) return new Set();
      const next = new Set(prev);
      companies.items.forEach(c => next.add(c.company_id));
      return next;
    });
  };

  const saveCompany = async (form) => {
    // Split cities from comma-separated string into array
    const payload = {
      name: form.name,
      sector: form.sector || "",
      careers_url: form.careers_url || "",
      parent_country: form.parent_country || "",
      cities: form.cities ? form.cities.split(",").map(s => s.trim()).filter(Boolean) : [],
      domain: form.domain || "",
      ats: form.ats || "",
      crawlable: form.crawlable !== false,
      india_headcount: form.india_headcount ? Number(form.india_headcount) : null,
    };
    try {
      if (form.company_id) {
        await api.patch(`/admin/directory/companies/${form.company_id}`, payload);
        toast.success(`Updated ${form.name}`);
      } else {
        await api.post("/admin/directory/companies", payload);
        toast.success(`Added ${form.name}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteCompanies = async (ids, alsoDeleteJobs = false) => {
    if (!ids.length) return;
    const label = ids.length === 1
      ? companies.items.find(c => c.company_id === ids[0])?.name || "this company"
      : `${ids.length} companies`;
    const msg = alsoDeleteJobs
      ? `Delete ${label} AND hard-delete all their job listings? This cannot be undone.`
      : `Delete ${label}? Their jobs will be marked inactive (kept for audit).`;
    if (!window.confirm(msg)) return;
    setBusyIds(prev => new Set([...prev, ...ids]));
    try {
      const r = await api.post("/admin/directory/companies/bulk-delete", { company_ids: ids, also_delete_jobs: alsoDeleteJobs });
      toast.success(`Deleted ${r.data.deleted} · ${alsoDeleteJobs ? `${r.data.jobs_deleted} jobs purged` : `${r.data.companies_marked_inactive} companies' jobs marked inactive`}`);
      setSelectedCompanyIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
    }
  };

  const load = useCallback(async () => {
    try {
      const requests = [
        api.get("/admin/directory/jobs", { params: { q, is_active: onlyActive ? true : undefined, limit: PAGE_SIZE, skip: page * PAGE_SIZE } }),
        api.get("/admin/directory/users", { params: { role: "candidate", q, limit: 300 } }),
        api.get("/admin/directory/users", { params: { role: "recruiter", q, limit: 300 } }),
        api.get("/admin/directory/companies", { params: { q, has_jobs: companiesWithJobs === null ? undefined : companiesWithJobs, limit: PAGE_SIZE, skip: companiesPage * PAGE_SIZE } }),
      ];
      const [j, s, r, c] = await Promise.all(requests);
      // Attach the SNAPSHOT page number to the companies response — this way the row
      // serial numbers on screen ALWAYS match the page that data actually came from,
      // even if the user has since clicked Next and companiesPage has advanced.
      setJobs(j.data); setSeekers(s.data); setRecruiters(r.data);
      setCompanies({ ...c.data, page: companiesPage });
    } catch {
      toast.error("Failed to load directory");
    }
  }, [q, onlyActive, page, companiesWithJobs, companiesPage]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="master-directory">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="eyebrow text-primary mb-1 inline-flex items-center gap-2">
            <Database className="h-3 w-3" /> Master Directory · read-only
          </p>
          <h2 className="text-2xl font-bold">Everything KARM knows, in one place.</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 border border-border rounded-lg bg-background">
            <SearchIcon className="h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="border-0 focus-visible:ring-0 shadow-none h-10 w-64" data-testid="directory-search" />
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full md:w-auto grid-cols-4" data-testid="directory-tabs">
          <TabsTrigger value="jobs" data-testid="dir-tab-jobs"><Briefcase className="mr-1.5 h-4 w-4" />Jobs ({jobs.total})</TabsTrigger>
          <TabsTrigger value="companies" data-testid="dir-tab-companies"><Building2 className="mr-1.5 h-4 w-4" />Companies ({companies.total.toLocaleString()})</TabsTrigger>
          <TabsTrigger value="seekers" data-testid="dir-tab-seekers"><Users className="mr-1.5 h-4 w-4" />Seekers ({seekers.total})</TabsTrigger>
          <TabsTrigger value="recruiters" data-testid="dir-tab-recruiters"><UserCog className="mr-1.5 h-4 w-4" />Recruiters ({recruiters.total})</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs">
          <div className="flex items-center justify-between mb-3 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} data-testid="dir-jobs-active" />
              <span>Active only</span>
            </label>
            {jobs.total > PAGE_SIZE && (
              <div className="flex items-center gap-2" data-testid="dir-jobs-pager">
                <span className="text-xs text-muted-foreground">
                  {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min(jobs.total, (page + 1) * PAGE_SIZE).toLocaleString()} of {jobs.total.toLocaleString()}
                </span>
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(0)} data-testid="dir-jobs-first">« First</Button>
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="dir-jobs-prev">‹ Prev</Button>
                <span className="text-xs text-muted-foreground px-1">
                  Page {page + 1} of {Math.max(1, Math.ceil(jobs.total / PAGE_SIZE))}
                </span>
                <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= jobs.total} onClick={() => setPage(p => p + 1)} data-testid="dir-jobs-next">Next ›</Button>
                <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= jobs.total} onClick={() => setPage(Math.max(0, Math.ceil(jobs.total / PAGE_SIZE) - 1))} data-testid="dir-jobs-last">Last »</Button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 px-3">Title</th>
                  <th className="py-2 px-3">Company</th>
                  <th className="py-2 px-3">Location</th>
                  <th className="py-2 px-3">Source</th>
                  <th className="py-2 px-3">Active</th>
                  <th className="py-2 px-3">Posted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.items.map((j) => (
                  <tr key={j.job_id} data-testid={`dir-job-${j.job_id}`}>
                    <td className="py-2 px-3 font-semibold">{j.title}</td>
                    <td className="py-2 px-3">{j.company}</td>
                    <td className="py-2 px-3 text-muted-foreground">{j.location}</td>
                    <td className="py-2 px-3"><span className="pill">{j.source}</span></td>
                    <td className="py-2 px-3">
                      {j.is_active
                        ? <span className="text-emerald-600 inline-flex items-center gap-1"><Check className="h-3 w-3" />yes</span>
                        : <span className="text-muted-foreground">inactive</span>}
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{j.posted_at ? new Date(j.posted_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="companies">
          <CompaniesDiscoveryWidget />
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-sm">
            <div className="flex items-center gap-2" data-testid="dir-companies-filter">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Filter:</span>
              <Button size="sm" variant={companiesWithJobs === null ? "default" : "outline"} onClick={() => setCompaniesWithJobs(null)} data-testid="dir-companies-all">All</Button>
              <Button size="sm" variant={companiesWithJobs === true ? "default" : "outline"} onClick={() => setCompaniesWithJobs(true)} data-testid="dir-companies-with-jobs">With live jobs</Button>
              <Button size="sm" variant={companiesWithJobs === false ? "default" : "outline"} onClick={() => setCompaniesWithJobs(false)} data-testid="dir-companies-no-jobs">Zero jobs</Button>
              <Button size="sm" onClick={() => setEditing({})} data-testid="dir-companies-add" className="ml-3">
                <Plus className="h-3.5 w-3.5 mr-1" />Add company
              </Button>
            </div>
            {companies.total > PAGE_SIZE && (
              <div className="flex items-center gap-2" data-testid="dir-companies-pager">
                <span className="text-xs text-muted-foreground">
                  {(companiesPage * PAGE_SIZE + 1).toLocaleString()}–{Math.min(companies.total, (companiesPage + 1) * PAGE_SIZE).toLocaleString()} of {companies.total.toLocaleString()}
                </span>
                <Button size="sm" variant="outline" disabled={companiesPage === 0} onClick={() => setCompaniesPage(0)} data-testid="dir-companies-first">« First</Button>
                <Button size="sm" variant="outline" disabled={companiesPage === 0} onClick={() => setCompaniesPage(p => p - 1)} data-testid="dir-companies-prev">‹ Prev</Button>
                <span className="text-xs text-muted-foreground px-1">
                  Page {companiesPage + 1} of {Math.max(1, Math.ceil(companies.total / PAGE_SIZE))}
                </span>
                <Button size="sm" variant="outline" disabled={(companiesPage + 1) * PAGE_SIZE >= companies.total} onClick={() => setCompaniesPage(p => p + 1)} data-testid="dir-companies-next">Next ›</Button>
                <Button size="sm" variant="outline" disabled={(companiesPage + 1) * PAGE_SIZE >= companies.total} onClick={() => setCompaniesPage(Math.max(0, Math.ceil(companies.total / PAGE_SIZE) - 1))} data-testid="dir-companies-last">Last »</Button>
              </div>
            )}
          </div>

          {/* Bulk-action bar — only appears when 1+ rows are checked */}
          {selectedCompanyIds.size > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-lg border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-sm" data-testid="dir-companies-bulk-bar">
              <span className="text-rose-700 dark:text-rose-300">
                <strong>{selectedCompanyIds.size}</strong> selected
              </span>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setSelectedCompanyIds(new Set())} data-testid="dir-companies-bulk-clear">Clear</Button>
                <Button size="sm" variant="destructive" onClick={() => deleteCompanies([...selectedCompanyIds], false)} data-testid="dir-companies-bulk-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" />Delete selected
                </Button>
                <Button size="sm" variant="destructive" className="opacity-80" onClick={() => deleteCompanies([...selectedCompanyIds], true)} data-testid="dir-companies-bulk-purge">
                  <Trash2 className="h-3.5 w-3.5 mr-1" />Delete + purge jobs
                </Button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 px-2 w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={companies.items.length > 0 && companies.items.every(c => selectedCompanyIds.has(c.company_id))}
                      onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                      data-testid="dir-companies-select-all"
                    />
                  </th>
                  <th className="py-2 px-2 w-10 text-right">#</th>
                  <th className="py-2 px-3">Company</th>
                  <th className="py-2 px-3">Sector</th>
                  <th className="py-2 px-3">Cities</th>
                  <th className="py-2 px-3">Country</th>
                  <th className="py-2 px-3">Careers</th>
                  <th className="py-2 px-3">Crawler</th>
                  <th className="py-2 px-3 text-right">Active jobs</th>
                  <th className="py-2 px-2 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {companies.items.length === 0 ? (
                  <tr><td colSpan={10} className="py-8 px-3 text-center text-muted-foreground text-xs">No companies match your filter.</td></tr>
                ) : companies.items.map((c, idx) => {
                  const selected = selectedCompanyIds.has(c.company_id);
                  const rowBusy = busyIds.has(c.company_id);
                  // Serial derives from the page THE DATA CAME FROM (`companies.page`)
                  // not the currently-selected `companiesPage`. This avoids any
                  // transient mismatch between the pager state and the visible rows.
                  const dataPage = typeof companies.page === "number" ? companies.page : companiesPage;
                  const serial = dataPage * PAGE_SIZE + idx + 1;
                  return (
                    <tr key={c.company_id} className={selected ? "bg-rose-500/5" : ""} data-testid={`dir-company-${c.company_id}`}>
                      <td className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleCompanySelect(c.company_id)}
                          aria-label={`Select ${c.name}`}
                          data-testid={`dir-company-check-${c.company_id}`}
                        />
                      </td>
                      <td className="py-2 px-2 text-right text-xs text-muted-foreground tabular-nums">{serial}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2.5">
                          {c.logo_url
                            ? <img src={c.logo_url} alt="" className="w-6 h-6 rounded-sm object-contain bg-white border border-border" onError={(e) => { e.target.style.display = "none"; }} />
                            : <div className="w-6 h-6 rounded-sm bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">{(c.name || "?").charAt(0).toUpperCase()}</div>
                          }
                          <span className="font-semibold">{c.name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{c.sector || "—"}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground" title={(c.cities || []).join(", ")}>
                        {(c.cities && c.cities.length) ? (c.cities.slice(0, 2).join(", ") + (c.cities.length > 2 ? ` +${c.cities.length - 2}` : "")) : "—"}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{c.parent_country || "—"}</td>
                      <td className="py-2 px-3">
                        {c.careers_url
                          ? <a href={c.careers_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs" data-testid={`dir-company-careers-${c.company_id}`}>
                              <ExternalLink className="h-3 w-3" />Open
                            </a>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 px-3">
                        {c.ats ? <span className="pill text-xs">{c.ats}</span> : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 px-3 text-right font-semibold tabular-nums">
                        {c.active_jobs > 0
                          ? <span className="text-emerald-600">{c.active_jobs.toLocaleString()}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(c)} title="Edit" data-testid={`dir-company-edit-${c.company_id}`}>
                            <Wand2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-rose-500/10 hover:text-rose-600" onClick={() => deleteCompanies([c.company_id], false)} disabled={rowBusy} title="Delete" data-testid={`dir-company-delete-${c.company_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add / Edit modal */}
          {editing && <CompanyFormDialog initial={editing} onCancel={() => setEditing(null)} onSave={saveCompany} />}
        </TabsContent>

        <TabsContent value="seekers">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3">Headline</th>
                  <th className="py-2 px-3">Skills</th>
                  <th className="py-2 px-3">Provider</th>
                  <th className="py-2 px-3">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {seekers.items.map((u) => (
                  <tr key={u.user_id} data-testid={`dir-seeker-${u.user_id}`}>
                    <td className="py-2 px-3 font-semibold">{u.name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{u.email}</td>
                    <td className="py-2 px-3 text-muted-foreground">{u.headline || "—"}</td>
                    <td className="py-2 px-3 text-xs">{(u.skills || []).slice(0, 3).join(", ")}{(u.skills || []).length > 3 ? "…" : ""}</td>
                    <td className="py-2 px-3"><span className="pill">{u.auth_provider}</span></td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="recruiters">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3">Provider</th>
                  <th className="py-2 px-3">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recruiters.items.map((u) => (
                  <tr key={u.user_id} data-testid={`dir-recruiter-${u.user_id}`}>
                    <td className="py-2 px-3 font-semibold">{u.name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{u.email}</td>
                    <td className="py-2 px-3"><span className="pill">{u.auth_provider}</span></td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}

// ---------------- Multi-GCC Crawler panel ----------------
function MultiCrawler() {
  const [items, setItems] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [runningAll, setRunningAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/crawlers");
      setItems(r.data.items || []);
      setTotalActive(r.data.total_active_crawled || 0);
    } catch (e) { console.error("crawlers load failed:", e); /* keep last good state */ }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [load]);

  const runOne = async (name) => {
    setLoading(true);
    try {
      await api.post("/admin/crawlers/run", { company_name: name });
      toast.success(`Queued: ${name}`);
      load();
    } catch {
      toast.error("Failed to queue");
    } finally {
      setLoading(false);
    }
  };

  const runAll = async () => {
    if (!window.confirm(`Run all ${items.length} safe crawlers sequentially? This can take 30–90 minutes. KARM will keep running normally during this time.`)) return;
    setRunningAll(true);
    try {
      await api.post("/admin/crawlers/run-all");
      toast.success("Sequential crawl queued. Status will update every 10s.");
    } catch {
      toast.error("Failed to queue run-all");
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="multi-crawler">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="eyebrow text-primary mb-1 inline-flex items-center gap-2">
            <Bot className="h-3 w-3" /> Safe crawlers · Workday + Greenhouse
          </p>
          <h2 className="text-2xl font-bold">{items.length} GCC job feeds · {totalActive.toLocaleString()} active roles</h2>
          <p className="text-sm text-muted-foreground mt-1">Read-only public APIs only. Crawls run sequentially (slow & respectful).</p>
        </div>
        <Button onClick={runAll} disabled={runningAll || items.length === 0} size="lg" data-testid="crawler-run-all">
          <RefreshCw className={`mr-2 h-4 w-4 ${runningAll ? "animate-spin" : ""}`} />
          Refresh all sequentially
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[640px] overflow-y-auto pr-2">
        {items.map((t) => (
          <div key={t.external_source} className="rounded-lg border border-border p-3 flex items-center gap-3" data-testid={`crawler-${t.external_source}`}>
            <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${t.active_jobs > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
              {t.ats === "workday" ? <Briefcase className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{t.company_name}</div>
              <div className="text-xs text-muted-foreground">
                {t.ats} · <span className="text-emerald-600 font-semibold">{t.active_jobs} active</span>
                {t.inactive_jobs > 0 && <span className="ml-1">· {t.inactive_jobs} inactive</span>}
                {t.last_run && <span className="ml-1">· last {new Date(t.last_run.started_at).toLocaleDateString()}</span>}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => runOne(t.company_name)} disabled={loading} data-testid={`crawler-run-${t.external_source}`}>
              <RefreshCw className="h-3 w-3 mr-1" /> Run
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------- Citi (unchanged — it has its own persistent run-history & scheduler) ----------------
function CitiCrawler() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const loadCiti = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/citi/status"),
        api.get("/admin/citi/runs?limit=10"),
      ]);
      setStatus(s.data);
      setRuns(r.data.items || []);
      setScheduleEnabled(!!s.data.enabled);
      setHour(s.data.hour ?? 6);
      setMinute(s.data.minute ?? 0);
    } catch { /* keep last good state */ }
  }, []);

  useEffect(() => {
    loadCiti();
    const id = setInterval(loadCiti, 5000);
    return () => clearInterval(id);
  }, [loadCiti]);

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/citi/refresh");
      if (r.data.started) toast.success("Citi crawl started.");
      else toast.info(r.data.message);
      loadCiti();
    } catch { toast.error("Failed to start"); }
    finally { setRefreshing(false); }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.post("/admin/citi/schedule", { enabled: scheduleEnabled, hour: parseInt(hour), minute: parseInt(minute) });
      toast.success("Schedule saved");
      loadCiti();
    } catch { toast.error("Failed"); }
    finally { setSavingSchedule(false); }
  };

  const lr = status?.last_run;

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="citi-crawler-section">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="eyebrow text-primary mb-1 inline-flex items-center gap-2"><Bot className="h-3 w-3" /> Deep crawler · Citi Workday</p>
          <h2 className="text-2xl font-bold">Citi GCC India · Master Feed (with descriptions)</h2>
          <p className="text-sm text-muted-foreground mt-1">Full job descriptions pulled per role. Scheduler + detailed run history.</p>
        </div>
        <Button onClick={triggerRefresh} disabled={refreshing || status?.running} size="lg" data-testid="citi-refresh-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${status?.running ? "animate-spin" : ""}`} />
          {status?.running ? "Crawling…" : "Refresh now"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatPill label="Active Citi roles" value={status?.active_jobs ?? "—"} accent />
        <StatPill label="Inactive (deactivated)" value={status?.inactive_jobs ?? "—"} />
        <StatPill label="Last run" value={lr ? `${lr.added}+ / ${lr.updated}~ / ${lr.deactivated}-` : "—"} />
        <StatPill label="Schedule" value={status?.enabled ? `${String(status.hour).padStart(2, "0")}:${String(status.minute).padStart(2, "0")} UTC` : "Off"} />
      </div>

      {status?.running && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 mb-6 flex items-center gap-3" data-testid="citi-running-banner">
          <Activity className="h-4 w-4 text-primary animate-pulse" />
          <div className="text-sm">
            <span className="font-semibold">Crawl in progress.</span>
            <span className="text-muted-foreground ml-2">Started {status.started_at ? new Date(status.started_at).toLocaleTimeString() : "just now"}.</span>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-semibold inline-flex items-center gap-2"><Clock className="h-4 w-4" />Auto-refresh</p>
            <p className="text-xs text-muted-foreground">Run the Citi crawler every morning at the same time.</p>
          </div>
          <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="citi-schedule-toggle" />
        </div>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div><Label>Hour (UTC, 0–23)</Label><Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} disabled={!scheduleEnabled} data-testid="citi-hour" /></div>
          <div><Label>Minute</Label><Input type="number" min={0} max={59} value={minute} onChange={(e) => setMinute(e.target.value)} disabled={!scheduleEnabled} data-testid="citi-minute" /></div>
          <Button onClick={saveSchedule} disabled={savingSchedule} data-testid="citi-schedule-save">{savingSchedule ? "Saving…" : "Save schedule"}</Button>
        </div>
        {status?.next_run_at && <div className="text-xs text-muted-foreground mt-3">Next run: {new Date(status.next_run_at).toLocaleString()}</div>}
      </div>

      <div>
        <p className="eyebrow mb-2">Recent runs</p>
        {runs.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No completed runs yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground uppercase tracking-widest">
                <tr><th className="py-2 pr-3">Started</th><th className="py-2 pr-3">Trigger</th><th className="py-2 pr-3">Postings</th><th className="py-2 pr-3">Added</th><th className="py-2 pr-3">Updated</th><th className="py-2 pr-3">Deactivated</th><th className="py-2 pr-3">Duration</th><th className="py-2 pr-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {runs.map((r) => (
                  <tr key={r.run_id} data-testid={`citi-run-${r.run_id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="py-2 pr-3"><span className="pill">{r.trigger}</span></td>
                    <td className="py-2 pr-3">{r.india_postings}</td>
                    <td className="py-2 pr-3">+{r.added}</td>
                    <td className="py-2 pr-3">{r.updated}</td>
                    <td className="py-2 pr-3">−{r.deactivated}</td>
                    <td className="py-2 pr-3">{r.duration_s}s</td>
                    <td className="py-2 pr-3">{r.errors?.length > 0 ? <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" />{r.errors.length}</span> : <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="h-3 w-3" />ok</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------- Import & Manual tools ----------------
function ImportTools() {
  const [importText, setImportText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [manual, setManual] = useState({ title: "", company: "", location: "", type: "Full-time", level: "", category: "", salary_min: "", salary_max: "", currency: "INR", skills: "", description: "", apply_url: "" });

  const importJob = async () => {
    if (!importText.trim() && !imagePreview) { toast.error("Paste text or attach an image"); return; }
    setCreating(true);
    try {
      const r = await api.post("/jobs/import", { source_text: importText || null, image_base64: imagePreview ? imagePreview.split(",")[1] : null });
      toast.success(`Imported: ${r.data.job.title}`);
      setImportText(""); setImagePreview(null); setImageFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setCreating(false); }
  };

  const onImageUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(f);
  };

  const createManual = async () => {
    setCreating(true);
    try {
      const payload = { ...manual, salary_min: manual.salary_min ? parseInt(manual.salary_min) : null, salary_max: manual.salary_max ? parseInt(manual.salary_max) : null, skills: manual.skills.split(",").map((s) => s.trim()).filter(Boolean) };
      await api.post("/jobs", payload);
      toast.success(`Posted: ${manual.title}`);
      setManual({ title: "", company: "", location: "", type: "Full-time", level: "", category: "", salary_min: "", salary_max: "", currency: "INR", skills: "", description: "", apply_url: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <section className="creative-zone p-6">
        <h2 className="text-lg font-bold mb-2 inline-flex items-center gap-2"><Wand2 className="h-4 w-4 text-accent" />AI Import</h2>
        <p className="text-sm text-muted-foreground mb-4">Paste JD or drop a screenshot — AI extracts a structured posting.</p>
        <Textarea rows={8} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder="Paste JD here…" data-testid="import-text" />
        <div className="mt-3">
          <input type="file" accept="image/*" onChange={onImageUpload} className="hidden" id="image-import" data-testid="import-image-input" />
          <label htmlFor="image-import" className="border-2 border-dashed border-border rounded-lg p-4 flex items-center justify-center gap-2 cursor-pointer hover:border-accent" data-testid="import-image-zone">
            <UploadCloud className="h-4 w-4" />
            <span className="text-sm">{imageFile ? imageFile.name : "Or drop a screenshot"}</span>
          </label>
        </div>
        <Button onClick={importJob} disabled={creating} className="w-full mt-4" data-testid="import-btn">{creating ? "Importing…" : "Import with AI"}</Button>
      </section>

      <section className="vital-card-static p-6">
        <h2 className="text-lg font-bold mb-2 inline-flex items-center gap-2"><Plus className="h-4 w-4" />Manual</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Title</Label><Input value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} data-testid="manual-title" /></div>
          <div><Label>Company</Label><Input value={manual.company} onChange={(e) => setManual({ ...manual, company: e.target.value })} data-testid="manual-company" /></div>
          <div><Label>Location</Label><Input value={manual.location} onChange={(e) => setManual({ ...manual, location: e.target.value })} data-testid="manual-location" /></div>
          <div><Label>Type</Label><Input value={manual.type} onChange={(e) => setManual({ ...manual, type: e.target.value })} /></div>
          <div><Label>Level</Label><Input value={manual.level} onChange={(e) => setManual({ ...manual, level: e.target.value })} /></div>
          <div><Label>Salary min</Label><Input type="number" value={manual.salary_min} onChange={(e) => setManual({ ...manual, salary_min: e.target.value })} /></div>
          <div><Label>Salary max</Label><Input type="number" value={manual.salary_max} onChange={(e) => setManual({ ...manual, salary_max: e.target.value })} /></div>
          <div className="col-span-2"><Label>Skills (comma)</Label><Input value={manual.skills} onChange={(e) => setManual({ ...manual, skills: e.target.value })} /></div>
          <div className="col-span-2"><Label>Apply URL</Label><Input value={manual.apply_url} onChange={(e) => setManual({ ...manual, apply_url: e.target.value })} /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={manual.description} onChange={(e) => setManual({ ...manual, description: e.target.value })} /></div>
        </div>
        <Button onClick={createManual} disabled={creating || !manual.title || !manual.company} className="w-full mt-4" data-testid="manual-create-btn">{creating ? "Posting…" : "Post job"}</Button>
      </section>
    </div>
  );
}

// ---------------- Page ----------------

// ---------------- Fresh Jobs (curated homepage grid) tab ----------------
function FreshJobsTab() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/fresh-jobs");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load fresh jobs");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/fresh-jobs/refresh");
      toast.success(`Rebuilt — ${r.data.total_picks} picks · ${new Date(r.data.generated_at).toLocaleTimeString()}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) return <div className="py-12 text-center text-muted-foreground">Loading fresh jobs…</div>;
  const cur = data.current;
  const lastRun = data.runs?.[0];

  return (
    <div className="space-y-4" data-testid="fresh-jobs-tab">
      <div className="vital-card-static p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="eyebrow mb-1">Homepage · daily batch</p>
            <h2 className="display-2 text-2xl md:text-3xl">Fresh on the desk — 3 jobs × 5 sectors</h2>
          </div>
          <Button onClick={refresh} disabled={refreshing} data-testid="fresh-jobs-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Rebuilding…" : "Rebuild now"}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="small-caps">Total picks</p>
            <p className="font-serif text-xl">{cur?.total_picks || 0} / 15</p>
          </div>
          <div>
            <p className="small-caps">Unique companies</p>
            <p className="font-serif text-xl">{cur?.uniqueness?.companies_used || 0}</p>
          </div>
          <div>
            <p className="small-caps">Last refreshed</p>
            <p className="font-serif text-xl">{cur?.generated_at ? new Date(cur.generated_at).toLocaleString() : "—"}</p>
          </div>
          <div>
            <p className="small-caps">Auto-refresh</p>
            <p className="font-serif text-xl">Daily · 02:30 UTC (08:00 IST)</p>
          </div>
        </div>
      </div>

      {(cur?.sections || []).map((s) => (
        <div key={s.slug} className="vital-card-static p-5" data-testid={`fresh-admin-section-${s.slug}`}>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-serif text-xl italic">{s.label}</h3>
            <span className="small-caps">{s.jobs.length} / 3 picks</span>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {s.jobs.map((j) => (
              <div key={j.job_id} className="border border-border rounded-sm p-3" data-testid={`fresh-admin-card-${j.job_id}`}>
                <p className="small-caps mb-1" style={{ color: "hsl(var(--primary))" }}>{j.tier_label}</p>
                <p className="font-serif text-base font-medium leading-tight mb-1">{j.title}</p>
                <p className="text-xs text-muted-foreground">{j.company}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{j.location}</p>
                {j.apply_url && (
                  <a href={j.apply_url} target="_blank" rel="noreferrer" className="text-[11px] mt-2 inline-flex items-center gap-1 hover:underline" style={{ color: "hsl(var(--primary))" }}>
                    apply ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {data.runs?.length > 0 && (
        <details className="vital-card-static p-5" data-testid="fresh-jobs-runs">
          <summary className="cursor-pointer small-caps">Run history · {data.runs.length}</summary>
          <ul className="mt-3 space-y-1.5 text-sm font-mono">
            {data.runs.map((r, i) => (
              <li key={i} className="flex items-center gap-3">
                <Check className="h-3 w-3 text-emerald-500" />
                <span>{new Date(r.run_at).toLocaleString()}</span>
                <span className="text-muted-foreground">· {r.total_picks} picks · {r.companies_used} unique companies</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {lastRun && (
        <p className="text-xs text-muted-foreground text-center">
          Picks the freshest active job per (sector × tier) with global no-repeat on company AND title. Tiers: <span className="font-semibold">junior</span> (Analyst / IC) · <span className="font-semibold">mid</span> (AVP / Lead / Manager) · <span className="font-semibold">senior</span> (VP / Director / Head).
        </p>
      )}
    </div>
  );
}

// ---------------- Market Data Feed tab ----------------
function MarketDataFeed() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("");

  const fmtBigUsd = (n) => {
    if (!n) return "—";
    if (n >= 1e12) return `$${(n / 1e12).toFixed(2)} T`;
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)} B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)} M`;
    return `$${Math.round(n).toLocaleString()}`;
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/market-data", { params: { limit: 200 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load market data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/market-data/refresh?top_n=100");
      toast.success(`Refreshed — ${r.data.added} entries cached (top: ${r.data.top_ticker})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) return <div className="py-12 text-center text-muted-foreground">Loading market data…</div>;
  const items = data.items.filter((it) =>
    !filter || (it.name + " " + it.ticker).toLowerCase().includes(filter.toLowerCase()),
  );
  const lastRun = data.runs?.[0];

  return (
    <div className="space-y-4" data-testid="market-data-tab">
      <div className="vital-card-static p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="eyebrow mb-1">Source · companiesmarketcap.com</p>
            <h2 className="display-2 text-2xl md:text-3xl">Top global employers — MCap · profit · stock</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={refresh} disabled={refreshing} data-testid="market-data-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh now"}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
          <div>
            <p className="small-caps">Cached</p>
            <p className="font-serif text-xl">{data.count} companies</p>
          </div>
          <div>
            <p className="small-caps">Last refreshed</p>
            <p className="font-serif text-xl">{lastRun?.run_at ? new Date(lastRun.run_at).toLocaleString() : "—"}</p>
          </div>
          <div>
            <p className="small-caps">Top by MCap</p>
            <p className="font-serif text-xl">{items[0]?.name || "—"}</p>
          </div>
          <div>
            <p className="small-caps">Auto-refresh</p>
            <p className="font-serif text-xl">Daily · 22:00 UTC</p>
          </div>
        </div>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or ticker…"
          className="w-full md:max-w-sm bg-background border border-border rounded-sm px-3 py-2 text-sm"
          data-testid="market-data-filter"
        />
      </div>

      <div className="vital-card-static overflow-x-auto">
        <table className="w-full text-sm" data-testid="market-data-table">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 small-caps">#</th>
              <th className="text-left p-3 small-caps">Company</th>
              <th className="text-left p-3 small-caps">Ticker</th>
              <th className="text-right p-3 small-caps">Market Cap</th>
              <th className="text-right p-3 small-caps">Profit (TTM)</th>
              <th className="text-right p-3 small-caps">Price</th>
              <th className="text-right p-3 small-caps">24h Δ</th>
              <th className="text-center p-3 small-caps">Country</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const up = (it.change_pct || 0) >= 0;
              return (
                <tr key={it.ticker} className="border-b border-border hover:bg-muted/40" data-testid={`market-row-${it.ticker}`}>
                  <td className="p-3 text-muted-foreground">{it.rank_market_cap}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {it.logo_url && (
                        <img src={it.logo_url} alt="" loading="lazy" className="h-6 w-6 object-contain" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                      )}
                      <span className="font-serif text-base">{it.name}</span>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs">{it.ticker}</td>
                  <td className="p-3 text-right font-mono font-semibold">{fmtBigUsd(it.market_cap_usd)}</td>
                  <td className="p-3 text-right font-mono">{fmtBigUsd(it.earnings_usd)}</td>
                  <td className="p-3 text-right font-mono">{it.price_usd ? `$${it.price_usd.toFixed(2)}` : "—"}</td>
                  <td className="p-3 text-right font-mono" style={{ color: up ? "#0a8a4a" : "hsl(var(--primary))" }}>
                    {typeof it.change_pct === "number" ? `${it.change_pct >= 0 ? "+" : ""}${it.change_pct.toFixed(2)}%` : "—"}
                  </td>
                  <td className="p-3 text-center text-lg">{it.country || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.runs?.length > 0 && (
        <details className="vital-card-static p-5" data-testid="market-data-runs">
          <summary className="cursor-pointer small-caps">Run history · {data.runs.length}</summary>
          <ul className="mt-3 space-y-1.5 text-sm font-mono">
            {data.runs.map((r, i) => (
              <li key={i} className="flex items-center gap-3">
                {r.ok ? <Check className="h-3 w-3 text-emerald-500" /> : <AlertTriangle className="h-3 w-3 text-amber-500" />}
                <span>{new Date(r.run_at).toLocaleString()}</span>
                <span className="text-muted-foreground">· added {r.added}</span>
                {r.top_ticker && <span className="text-muted-foreground">· top {r.top_ticker}</span>}
                {r.error && <span className="text-red-500">· {r.error}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ---------------- Crawl Coverage tab ----------------
function CrawlCoverage() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/crawlers/coverage");
      setData(r.data);
    } catch {
      toast.error("Failed to load coverage");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/crawlers/run-all");
      toast.success(`Started ${r.data.count} crawlers — running sequentially. Refresh in a few minutes.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally {
      setTimeout(() => setRunning(false), 1500);
    }
  };

  const reimport = async () => {
    try {
      const r = await api.get("/admin/import-gcc-master");
      toast.success(`Master directory re-imported · added ${r.data.added}, updated ${r.data.updated}`);
      load();
    } catch {
      toast.error("Re-import failed");
    }
  };

  if (!data) return <div className="text-muted-foreground" data-testid="coverage-loading">Loading coverage…</div>;

  const covPct = data.total_companies ? Math.round((data.covered / data.total_companies) * 100) : 0;
  const livePct = data.total_companies ? Math.round((data.with_live_jobs / data.total_companies) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="coverage-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="GCCs in directory" value={data.total_companies} />
        <StatPill label="Have a crawl feed" value={`${data.covered} (${covPct}%)`} accent />
        <StatPill label="With live jobs today" value={`${data.with_live_jobs} (${livePct}%)`} accent />
        <StatPill label="No feed yet" value={data.uncovered.length} />
      </div>

      <div className="flex flex-wrap gap-3" data-testid="coverage-actions">
        <Button onClick={runAll} disabled={running} data-testid="coverage-run-all">
          <RefreshCw className={`mr-2 h-4 w-4 ${running ? "animate-spin" : ""}`} />Re-run all safe crawlers
        </Button>
        <Button variant="outline" onClick={reimport} data-testid="coverage-reimport">
          <Database className="mr-2 h-4 w-4" />Re-import master directory
        </Button>
        <Button variant="ghost" onClick={load} data-testid="coverage-refresh">
          Refresh
        </Button>
      </div>

      <details className="vital-card-static p-4" open data-testid="coverage-uncovered">
        <summary className="cursor-pointer font-bold inline-flex items-center gap-2">
          <Eye className="h-4 w-4" /> {data.uncovered.length} GCCs without a configured safe-crawl feed
          <span className="text-xs text-muted-foreground font-normal">(applications still link out to their careers page)</span>
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground uppercase">
              <tr><th className="text-left py-1 px-2">Company</th><th className="text-left py-1 px-2">Sector</th><th className="text-left py-1 px-2">Careers URL</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.uncovered.slice(0, 100).map((c) => (
                <tr key={c.name} data-testid={`uncovered-${c.name}`}>
                  <td className="py-1.5 px-2 font-semibold">{c.name}</td>
                  <td className="py-1.5 px-2 text-muted-foreground">{c.sector}</td>
                  <td className="py-1.5 px-2 text-xs">
                    {c.careers_url
                      ? <a href={c.careers_url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate inline-block max-w-md">{c.careers_url}</a>
                      : <span className="text-muted-foreground italic">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.uncovered.length > 100 && <p className="text-xs text-muted-foreground mt-3">+ {data.uncovered.length - 100} more …</p>}
        </div>
      </details>

      {data.covered_no_jobs.length > 0 && (
        <details className="vital-card-static p-4" data-testid="coverage-zero-jobs">
          <summary className="cursor-pointer font-bold inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> {data.covered_no_jobs.length} configured feeds that returned ZERO India jobs in the last run
            <span className="text-xs text-muted-foreground font-normal">(tenant URL might be wrong / company blocked our IP / no India roles open right now)</span>
          </summary>
          <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {data.covered_no_jobs.map((c) => <li key={c.name} className="py-1">• <span className="font-semibold">{c.name}</span> <span className="text-muted-foreground text-xs">— {c.reason}</span></li>)}
          </ul>
        </details>
      )}

      {(data.listing_only?.length || 0) > 0 && (
        <details className="vital-card-static p-4" data-testid="coverage-listing-only">
          <summary className="cursor-pointer font-bold inline-flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-blue-500" /> {data.listing_only.length} listing-only companies (ATS walled — deep-link to careers site)
            <span className="text-xs text-muted-foreground font-normal">(Phenom anti-bot, Cloudflare-gated, IBM Kenexa session, etc.)</span>
          </summary>
          <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {data.listing_only.map((c) => (
              <li key={c.name} className="py-1">
                • <span className="font-semibold">{c.name}</span>{" "}
                <span className="text-muted-foreground text-xs">— {c.reason}</span>{" "}
                {c.careers_url && <a href={c.careers_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">careers ↗</a>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


// ---------------- Content Studio Ops tab ----------------
function ContentStudioOps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/content-studio/status");
      setData(r.data);
    } catch {
      toast.error("Failed to load Content Studio status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runProbe = async () => {
    setProbing(true);
    try {
      const r = await api.post("/admin/content-studio/probe", {}, { timeout: 90000 });
      const results = r.data?.results || {};
      const ok = Object.values(results).filter(v => v.ok).length;
      toast.success(`${ok}/${Object.keys(results).length} models healthy`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Probe failed");
    } finally {
      setProbing(false);
    }
  };

  const ackBudget = async () => {
    try {
      await api.post("/admin/content-studio/budget/clear");
      toast.success("Budget events cleared");
      load();
    } catch {
      toast.error("Failed to clear");
    }
  };

  const budget = data?.budget;
  const exceeded = budget?.status === "exceeded_recently";
  const models = data?.models || [];

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="content-studio-ops">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="eyebrow text-primary mb-1 inline-flex items-center gap-2">
            <Cpu className="h-3 w-3" /> Content Studio · live status
          </p>
          <h2 className="text-2xl font-bold">AI models powering Content Studio &amp; Concierge.</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Universal key in use: <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{data?.key_masked || "—"}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="cs-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={runProbe} disabled={probing} data-testid="cs-probe">
            <Zap className={`h-4 w-4 mr-1.5 ${probing ? "animate-pulse" : ""}`} />
            {probing ? "Probing…" : "Re-probe all models"}
          </Button>
        </div>
      </div>

      {/* Budget banner */}
      <div
        className={`rounded-lg border p-4 mb-5 flex flex-wrap items-center justify-between gap-3 ${
          exceeded ? "border-destructive/40 bg-destructive/5" : "border-emerald-500/30 bg-emerald-500/5"
        }`}
        data-testid="cs-budget-banner"
      >
        <div className="flex items-start gap-3">
          {exceeded
            ? <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
            : <Check className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />}
          <div>
            <div className={`text-sm font-semibold ${exceeded ? "text-destructive" : "text-emerald-700"}`}>
              {exceeded ? "Universal Key budget recently exhausted" : "Universal Key budget healthy"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{budget?.label || "—"}</div>
            {budget?.last_event && (
              <div className="text-[11px] text-muted-foreground mt-1">
                Last event: {new Date(budget.last_event.at).toLocaleString()} · {budget.last_event.model}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant={exceeded ? "default" : "outline"} data-testid="cs-topup">
            <a href={budget?.top_up_url || "#"} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Top up balance
            </a>
          </Button>
          {exceeded && (
            <Button variant="ghost" size="sm" onClick={ackBudget} data-testid="cs-ack-budget">
              Acknowledge
            </Button>
          )}
        </div>
      </div>

      {/* Models table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
              <th className="py-2.5 px-3">Model</th>
              <th className="py-2.5 px-3">Type</th>
              <th className="py-2.5 px-3">Role</th>
              <th className="py-2.5 px-3">Provider</th>
              <th className="py-2.5 px-3">Interface</th>
              <th className="py-2.5 px-3">Key</th>
              <th className="py-2.5 px-3">Status</th>
              <th className="py-2.5 px-3">Latency</th>
              <th className="py-2.5 px-3">Last probed</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => {
              const p = m.last_probe;
              const TypeIcon = m.type === "image" ? ImageIcon : FileText;
              return (
                <tr key={m.id} className="border-t border-border align-top" data-testid={`cs-row-${m.id}`}>
                  <td className="py-3 px-3 font-semibold">
                    <div>{m.label}</div>
                    <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{m.id}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{m.used_in.join(" · ")}</div>
                  </td>
                  <td className="py-3 px-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-muted">
                      <TypeIcon className="h-3 w-3" /> {m.type}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] ${
                      m.role === "primary" ? "bg-primary/10 text-primary"
                      : m.role === "fallback" ? "bg-amber-500/10 text-amber-700"
                      : "bg-muted text-muted-foreground"
                    }`}>{m.role}</span>
                  </td>
                  <td className="py-3 px-3 text-xs">{m.provider}</td>
                  <td className="py-3 px-3 text-[11px] font-mono text-muted-foreground">{m.interface}</td>
                  <td className="py-3 px-3 text-[11px] font-mono">{m.key_masked}</td>
                  <td className="py-3 px-3">
                    {p ? (
                      p.ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-xs">
                          <Check className="h-3.5 w-3.5" /> Healthy
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-destructive font-semibold text-xs" title={p.error}>
                          <AlertTriangle className="h-3.5 w-3.5" /> Failing
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">Not probed</span>
                    )}
                    {p && !p.ok && p.error && (
                      <div className="text-[10px] text-destructive/80 mt-1 max-w-[260px] truncate" title={p.error}>
                        {p.error}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-3 text-xs text-muted-foreground">
                    {p ? `${p.latency_ms}ms` : "—"}
                  </td>
                  <td className="py-3 px-3 text-xs text-muted-foreground">
                    {p?.at ? new Date(p.at).toLocaleTimeString() : "—"}
                  </td>
                </tr>
              );
            })}
            {!models.length && (
              <tr><td colSpan={9} className="py-6 px-3 text-center text-sm text-muted-foreground">
                {loading ? "Loading…" : "No models registered"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recent events */}
      {(data?.recent_events || []).length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Recent budget-exhaustion events
          </h3>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left text-[11px] text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 px-3">When</th>
                  <th className="py-2 px-3">Model</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_events.map((e, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-2 px-3 whitespace-nowrap">{new Date(e.at).toLocaleString()}</td>
                    <td className="py-2 px-3 font-mono">{e.model}</td>
                    <td className="py-2 px-3">{e.type}</td>
                    <td className="py-2 px-3 text-muted-foreground truncate max-w-[480px]" title={e.error}>{e.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}



// ---------------- Book of Work tab ----------------
const BOW_AREAS = ["auth","crawlers","content_studio","byok","admin","payments","concierge","jobs","companies","email","market_data","fresh_jobs","design","infra","other"];
const BOW_PRIOS = ["P0","P1","P2","P3"];
const BOW_STATUSES = ["open","in_progress","completed","resolved","archived"];

function priorityPill(p) {
  const cls = {
    P0: "bg-destructive/15 text-destructive border-destructive/30",
    P1: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    P2: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    P3: "bg-muted text-muted-foreground border-border",
  }[p] || "bg-muted";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${cls}`}>{p}</span>;
}

function statusPill(s) {
  const cls = {
    open: "bg-amber-500/10 text-amber-700",
    in_progress: "bg-blue-500/10 text-blue-700",
    completed: "bg-emerald-500/10 text-emerald-700",
    resolved: "bg-emerald-500/10 text-emerald-700",
    archived: "bg-muted text-muted-foreground",
  }[s] || "bg-muted";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{s.replace("_"," ")}</span>;
}

function BookOfWork() {
  const [data, setData] = useState({ items: [], counts: { enhancement: {}, defect: {} } });
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterArea, setFilterArea] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ kind: "enhancement", title: "", description: "", priority: "P2", area: "other" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterKind !== "all") params.set("kind", filterKind);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterArea !== "all") params.set("area", filterArea);
      const r = await api.get(`/admin/book-of-work?${params.toString()}`);
      setData(r.data || { items: [], counts: {} });
    } catch { toast.error("Failed to load Book of Work"); }
    finally { setLoading(false); }
  }, [filterKind, filterStatus, filterArea]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/book-of-work/${id}`, { status });
      toast.success(`Marked ${status.replace("_"," ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/book-of-work/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/admin/book-of-work", form);
      toast.success("Item added");
      setShowCreate(false);
      setForm({ kind: "enhancement", title: "", description: "", priority: "P2", area: "other" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const seed = async () => {
    try {
      const r = await api.post("/admin/book-of-work/bulk-seed");
      toast.success(`Seeded ${r.data?.added || 0} backlog items`);
      load();
    } catch { toast.error("Seed failed"); }
  };

  const enh = data.counts?.enhancement || {};
  const def = data.counts?.defect || {};

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="book-of-work">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="eyebrow text-primary mb-1 inline-flex items-center gap-2">
            <ClipboardList className="h-3 w-3" /> Book of Work
          </p>
          <h2 className="text-2xl font-bold">Enhancements &amp; Defects — single source of truth.</h2>
          <div className="text-sm text-muted-foreground mt-1 space-x-3">
            <span>Enhancements: {(enh.open||0)} open · {(enh.in_progress||0)} in-flight · <span className="text-emerald-600">{(enh.completed||0)} ✓ completed</span></span>
            <span>·</span>
            <span>Defects: <span className="text-destructive font-semibold">{(def.open||0)} open</span> · {(def.in_progress||0)} in-flight · <span className="text-emerald-600">{(def.resolved||0)} ✓ resolved</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="bow-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={seed} data-testid="bow-seed">Seed backlog</Button>
          <Button size="sm" onClick={() => setShowCreate(v => !v)} data-testid="bow-add">
            <Plus className="h-4 w-4 mr-1" /> Add item
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} className="h-8 px-2 rounded-sm bg-background border border-border text-xs" data-testid="bow-filter-kind">
          <option value="all">All kinds</option>
          <option value="enhancement">Enhancements</option>
          <option value="defect">Defects</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 px-2 rounded-sm bg-background border border-border text-xs" data-testid="bow-filter-status">
          <option value="all">All statuses</option>
          {BOW_STATUSES.map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
        </select>
        <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)} className="h-8 px-2 rounded-sm bg-background border border-border text-xs" data-testid="bow-filter-area">
          <option value="all">All areas</option>
          {BOW_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rounded-lg border border-border p-4 mb-4 bg-muted/30" data-testid="bow-create-form">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
            <select value={form.kind} onChange={(e) => setForm(f => ({...f, kind: e.target.value}))} className="h-9 px-2 rounded-sm bg-background border border-border text-sm" data-testid="bow-new-kind">
              <option value="enhancement">Enhancement</option>
              <option value="defect">Defect</option>
            </select>
            <select value={form.priority} onChange={(e) => setForm(f => ({...f, priority: e.target.value}))} className="h-9 px-2 rounded-sm bg-background border border-border text-sm" data-testid="bow-new-priority">
              {BOW_PRIOS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={form.area} onChange={(e) => setForm(f => ({...f, area: e.target.value}))} className="h-9 px-2 rounded-sm bg-background border border-border text-sm" data-testid="bow-new-area">
              {BOW_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <input value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} placeholder="Short title" className="md:col-span-2 h-9 px-2 rounded-sm bg-background border border-border text-sm" data-testid="bow-new-title" />
          </div>
          <textarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} placeholder="Description (optional)" className="w-full h-16 p-2 rounded-sm bg-background border border-border text-sm mb-2" data-testid="bow-new-desc" />
          <div className="flex gap-2">
            <Button size="sm" onClick={create} data-testid="bow-create-save">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Items table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
              <th className="py-2.5 px-3 w-[60px]">Kind</th>
              <th className="py-2.5 px-3 w-[60px]">Pri</th>
              <th className="py-2.5 px-3">Title</th>
              <th className="py-2.5 px-3 w-[120px]">Area</th>
              <th className="py-2.5 px-3 w-[110px]">Status</th>
              <th className="py-2.5 px-3 w-[220px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.id} className="border-t border-border align-top" data-testid={`bow-row-${it.id}`}>
                <td className="py-3 px-3">
                  {it.kind === "defect"
                    ? <Bug className="h-4 w-4 text-destructive" title="defect" />
                    : <Sparkles className="h-4 w-4 text-primary" title="enhancement" />}
                </td>
                <td className="py-3 px-3">{priorityPill(it.priority)}</td>
                <td className="py-3 px-3">
                  <div className="font-semibold">{it.title}</div>
                  {it.description && <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{it.description}</div>}
                </td>
                <td className="py-3 px-3 text-xs text-muted-foreground">{it.area}</td>
                <td className="py-3 px-3">{statusPill(it.status)}</td>
                <td className="py-3 px-3">
                  <div className="flex flex-wrap items-center gap-1">
                    {it.status === "open" && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => updateStatus(it.id, "in_progress")} data-testid={`bow-start-${it.id}`}>
                        Start
                      </Button>
                    )}
                    {(it.status === "open" || it.status === "in_progress") && (
                      <Button size="sm" className="h-7 text-xs" onClick={() => updateStatus(it.id, it.kind === "defect" ? "resolved" : "completed")} data-testid={`bow-done-${it.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> {it.kind === "defect" ? "Resolve" : "Complete"}
                      </Button>
                    )}
                    {(it.status === "completed" || it.status === "resolved") && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => updateStatus(it.id, "open")} data-testid={`bow-reopen-${it.id}`}>
                        Re-open
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => remove(it.id)} data-testid={`bow-delete-${it.id}`} title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!data.items.length && (
              <tr><td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                {loading ? "Loading…" : "No items match these filters"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}



function InternalAdsAdmin() {
  const [ads, setAds] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // ad_id being edited
  const [form, setForm] = useState({
    name: "", slot: "", headline: "", body: "",
    cta_label: "Learn more", cta_url: "", image_url: "",
    is_active: true, weight: 1, starts_at: "", ends_at: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/internal-ads"),
        api.get("/admin/internal-ads/slots"),
      ]);
      setAds(a.data.items || []);
      setSlots(s.data.slots || []);
      if (!form.slot && s.data.slots?.length) setForm((f) => ({ ...f, slot: s.data.slots[0].key }));
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: "", slot: slots[0]?.key || "", headline: "", body: "",
      cta_label: "Learn more", cta_url: "", image_url: "",
      is_active: true, weight: 1, starts_at: "", ends_at: "",
    });
  };

  const save = async () => {
    // Sanitise empty date strings — backend expects null, not "".
    const payload = { ...form, starts_at: form.starts_at || null, ends_at: form.ends_at || null };
    try {
      if (editing) {
        await api.patch(`/admin/internal-ads/${editing}`, payload);
        toast.success("Ad updated");
      } else {
        await api.post("/admin/internal-ads", payload);
        toast.success("Ad created");
      }
      resetForm(); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (ad_id) => {
    if (!confirm("Delete this ad?")) return;
    await api.delete(`/admin/internal-ads/${ad_id}`);
    toast.success("Deleted");
    await load();
  };
  const toggle = async (ad) => {
    await api.patch(`/admin/internal-ads/${ad.ad_id}`, { is_active: !ad.is_active });
    await load();
  };
  const startEdit = (ad) => {
    setEditing(ad.ad_id);
    setForm({
      name: ad.name || "", slot: ad.slot || "", headline: ad.headline || "",
      body: ad.body || "", cta_label: ad.cta_label || "Learn more",
      cta_url: ad.cta_url || "", image_url: ad.image_url || "",
      is_active: !!ad.is_active, weight: ad.weight || 1,
      starts_at: ad.starts_at || "", ends_at: ad.ends_at || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="internal-ads-admin">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="text-xl font-black">Internal Ads</h2>
          <p className="text-xs text-muted-foreground">
            KARM-controlled promotions (e.g. Karmpatra Workletter, Content Studio, Concierge).
            Ads render only where a matching &lt;InternalAd slot=&quot;…&quot;/&gt; is mounted.
          </p>
        </div>
        {editing && (
          <button onClick={resetForm} className="text-xs underline" data-testid="ads-cancel-edit">
            Cancel edit
          </button>
        )}
      </div>

      {/* Editor */}
      <div className="grid md:grid-cols-2 gap-3 mb-6 p-4 border rounded-sm bg-muted/30" data-testid="ads-editor">
        <label className="text-xs">Internal name
          <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="Karmpatra Workletter Sept"
                 data-testid="ads-input-name" />
        </label>
        <label className="text-xs">Slot
          <select value={form.slot} onChange={(e) => setForm({...form, slot: e.target.value})}
                  className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                  data-testid="ads-input-slot">
            {slots.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-xs md:col-span-2">Headline
          <input value={form.headline} onChange={(e) => setForm({...form, headline: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="Karmpatra — the weekly memo elite GCC leaders read"
                 maxLength={100}
                 data-testid="ads-input-headline" />
        </label>
        <label className="text-xs md:col-span-2">Body (optional, ≤280 chars)
          <textarea value={form.body} onChange={(e) => setForm({...form, body: e.target.value})}
                    rows={2} className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                    placeholder="Curated GCC hiring signals, market pulse, and career playbooks every Monday."
                    maxLength={280}
                    data-testid="ads-input-body" />
        </label>
        <label className="text-xs">CTA label
          <input value={form.cta_label} onChange={(e) => setForm({...form, cta_label: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 data-testid="ads-input-cta-label" />
        </label>
        <label className="text-xs">CTA URL
          <input value={form.cta_url} onChange={(e) => setForm({...form, cta_url: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="https://karm.day/karmpatra"
                 data-testid="ads-input-cta-url" />
        </label>
        <label className="text-xs md:col-span-2">Image URL (optional)
          <input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="https://karm.day/assets/karmpatra.jpg"
                 data-testid="ads-input-image-url" />
        </label>
        <label className="text-xs">Weight (1-100, higher = shown more)
          <input type="number" value={form.weight} min={1} max={100}
                 onChange={(e) => setForm({...form, weight: Math.max(1, parseInt(e.target.value)||1)})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 data-testid="ads-input-weight" />
        </label>
        <label className="text-xs flex items-center gap-2 mt-4">
          <input type="checkbox" checked={form.is_active}
                 onChange={(e) => setForm({...form, is_active: e.target.checked})}
                 data-testid="ads-input-active" />
          Active
        </label>
        <label className="text-xs">Starts at (optional, ISO)
          <input value={form.starts_at} onChange={(e) => setForm({...form, starts_at: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="2026-02-10T00:00:00Z"
                 data-testid="ads-input-starts" />
        </label>
        <label className="text-xs">Ends at (optional, ISO)
          <input value={form.ends_at} onChange={(e) => setForm({...form, ends_at: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="2026-03-10T00:00:00Z"
                 data-testid="ads-input-ends" />
        </label>
        <div className="md:col-span-2 flex justify-end">
          <button onClick={save} className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm"
                  data-testid="ads-save-btn">
            {editing ? "Update ad" : "Create ad"}
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : ads.length === 0 ? (
        <div className="text-xs text-muted-foreground">No ads yet. Create your first above.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ads-table">
            <thead className="border-b">
              <tr className="text-left">
                <th className="py-2 pr-2">Name</th>
                <th className="py-2 pr-2">Slot</th>
                <th className="py-2 pr-2">Headline</th>
                <th className="py-2 pr-2">Active</th>
                <th className="py-2 pr-2">Wgt</th>
                <th className="py-2 pr-2">Impr</th>
                <th className="py-2 pr-2">Clicks</th>
                <th className="py-2 pr-2">CTR</th>
                <th className="py-2 pr-2 text-right">—</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((a) => {
                const ctr = a.impressions ? ((a.clicks || 0) / a.impressions * 100).toFixed(1) : "—";
                const slotLbl = slots.find((s) => s.key === a.slot)?.label || a.slot;
                return (
                  <tr key={a.ad_id} className="border-b" data-testid={`ads-row-${a.ad_id}`}>
                    <td className="py-2 pr-2 font-medium">{a.name}</td>
                    <td className="py-2 pr-2 text-muted-foreground">{slotLbl}</td>
                    <td className="py-2 pr-2 max-w-[280px] truncate">{a.headline}</td>
                    <td className="py-2 pr-2">
                      <button onClick={() => toggle(a)}
                              className={"text-[10px] px-1.5 py-0.5 rounded-sm " + (a.is_active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground")}
                              data-testid={`ads-toggle-${a.ad_id}`}>
                        {a.is_active ? "ON" : "OFF"}
                      </button>
                    </td>
                    <td className="py-2 pr-2">{a.weight}</td>
                    <td className="py-2 pr-2">{a.impressions || 0}</td>
                    <td className="py-2 pr-2">{a.clicks || 0}</td>
                    <td className="py-2 pr-2">{ctr === "—" ? "—" : `${ctr}%`}</td>
                    <td className="py-2 pr-2 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(a)} className="underline mr-2" data-testid={`ads-edit-${a.ad_id}`}>edit</button>
                      <button onClick={() => del(a.ad_id)} className="text-destructive underline" data-testid={`ads-delete-${a.ad_id}`}>del</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}



function KarmpatraAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // issue_id
  const [form, setForm] = useState({
    title: "", dek: "", author: "KARM Editorial",
    html: "", cover_image_url: "", tags: "",
    is_published: true, published_at: "", slug: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/karmpatra");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      title: "", dek: "", author: "KARM Editorial",
      html: "", cover_image_url: "", tags: "",
      is_published: true, published_at: "", slug: "",
    });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.html?$/i.test(f.name)) {
      toast.error("Upload a .html file");
      return;
    }
    const text = await f.text();
    setForm((prev) => ({ ...prev, html: text }));
    toast.success(`Loaded ${f.name} (${(f.size / 1024).toFixed(1)} KB)`);
  };

  const save = async () => {
    if (!form.title.trim() || !form.html.trim()) {
      toast.error("Title and HTML body are required");
      return;
    }
    const payload = {
      title: form.title.trim(),
      dek: form.dek.trim(),
      author: form.author.trim() || "KARM Editorial",
      html: form.html,
      cover_image_url: form.cover_image_url.trim() || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      is_published: !!form.is_published,
      published_at: form.published_at || null,
      slug: form.slug.trim() || null,
    };
    try {
      if (editing) {
        await api.patch(`/admin/karmpatra/${editing}`, payload);
        toast.success("Letter updated");
      } else {
        const r = await api.post("/admin/karmpatra", payload);
        toast.success(`Letter No. ${String(r.data.issue_number).padStart(2, "0")} published`);
      }
      resetForm(); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (issue_id, num) => {
    if (!confirm(`Delete letter No. ${String(num).padStart(2, "0")}?`)) return;
    await api.delete(`/admin/karmpatra/${issue_id}`);
    toast.success("Deleted");
    await load();
  };
  const toggle = async (it) => {
    await api.patch(`/admin/karmpatra/${it.issue_id}`, { is_published: !it.is_published });
    await load();
  };
  const startEdit = async (issue_id) => {
    const r = await api.get(`/admin/karmpatra/${issue_id}`);
    const it = r.data;
    setEditing(issue_id);
    setForm({
      title: it.title || "", dek: it.dek || "", author: it.author || "KARM Editorial",
      html: it.html || "", cover_image_url: it.cover_image_url || "",
      tags: (it.tags || []).join(", "),
      is_published: !!it.is_published,
      published_at: it.published_at || "",
      slug: it.slug || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="vital-card-static p-6 md:p-8" data-testid="karmpatra-admin">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="text-xl font-black">Karmpatra — Editorial Letters</h2>
          <p className="text-xs text-muted-foreground">
            Paste HTML directly, or upload a .html file. Letters are auto-numbered.
            Read-only public archive at <a href="/karmpatra" target="_blank" rel="noopener noreferrer" className="underline">/karmpatra</a>.
          </p>
        </div>
        {editing && (
          <button onClick={resetForm} className="text-xs underline" data-testid="kp-cancel-edit">
            Cancel edit
          </button>
        )}
      </div>

      {/* Editor */}
      <div className="grid gap-3 mb-6 p-4 border rounded-sm bg-muted/30" data-testid="kp-editor">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-xs">Title *
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                   maxLength={160}
                   className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                   placeholder="The word that moves the world"
                   data-testid="kp-input-title" />
          </label>
          <label className="text-xs">Author
            <input value={form.author} onChange={(e) => setForm({...form, author: e.target.value})}
                   className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                   data-testid="kp-input-author" />
          </label>
        </div>
        <label className="text-xs">Dek (subtitle / hook, ≤240 chars)
          <input value={form.dek} onChange={(e) => setForm({...form, dek: e.target.value})}
                 maxLength={240}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="A meditation on the mantras that reshape how you show up at work."
                 data-testid="kp-input-dek" />
        </label>

        <div className="flex items-center gap-3">
          <label className="text-xs flex-1">Upload .html file
            <input type="file" accept=".html,.htm" onChange={onFile}
                   className="w-full mt-1 text-xs"
                   data-testid="kp-input-file" />
          </label>
          <span className="text-[10px] text-muted-foreground pt-4">— or paste below —</span>
        </div>
        <label className="text-xs">Body HTML *
          <textarea value={form.html} onChange={(e) => setForm({...form, html: e.target.value})}
                    rows={12}
                    className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-xs font-mono"
                    placeholder="<p>Your full HTML content here — scripts and event handlers are stripped at render time by DOMPurify.</p>"
                    data-testid="kp-input-html" />
          <span className="block mt-1 text-[10px] text-muted-foreground">
            {form.html ? `${form.html.length.toLocaleString()} chars` : "empty"}
            {" · "}Scripts + inline event handlers are auto-stripped before render.
          </span>
        </label>

        <div className="grid md:grid-cols-3 gap-3">
          <label className="text-xs">Tags (comma-separated)
            <input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}
                   className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                   placeholder="GCC, hiring, craft"
                   data-testid="kp-input-tags" />
          </label>
          <label className="text-xs">Slug (auto if blank)
            <input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})}
                   className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                   placeholder="the-word-that-moves-the-world"
                   data-testid="kp-input-slug" />
          </label>
          <label className="text-xs">Publish date (optional ISO)
            <input value={form.published_at} onChange={(e) => setForm({...form, published_at: e.target.value})}
                   className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                   placeholder="2026-02-07T09:00:00Z"
                   data-testid="kp-input-published-at" />
          </label>
        </div>
        <label className="text-xs">Cover image URL (optional)
          <input value={form.cover_image_url} onChange={(e) => setForm({...form, cover_image_url: e.target.value})}
                 className="w-full mt-1 px-2 py-1.5 border rounded-sm bg-background text-sm"
                 placeholder="https://karm.day/assets/letter-01-hero.jpg"
                 data-testid="kp-input-cover-image" />
        </label>

        <div className="flex items-center gap-4">
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={form.is_published}
                   onChange={(e) => setForm({...form, is_published: e.target.checked})}
                   data-testid="kp-input-published" />
            Published (visible on /karmpatra)
          </label>
          <div className="flex-1" />
          <button onClick={save}
                  className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm"
                  data-testid="kp-save-btn">
            {editing ? "Update letter" : "Publish letter"}
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground">No letters yet. Publish your first above.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="kp-table">
            <thead className="border-b">
              <tr className="text-left">
                <th className="py-2 pr-2 w-16">No.</th>
                <th className="py-2 pr-2">Title</th>
                <th className="py-2 pr-2">Published</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Slug</th>
                <th className="py-2 pr-2 text-right">—</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.issue_id} className="border-b" data-testid={`kp-row-${it.issue_number}`}>
                  <td className="py-2 pr-2 font-medium">
                    No.&nbsp;{String(it.issue_number).padStart(2, "0")}
                  </td>
                  <td className="py-2 pr-2 max-w-[320px] truncate">{it.title}</td>
                  <td className="py-2 pr-2 text-muted-foreground">
                    {it.published_at ? new Date(it.published_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2 pr-2">
                    <button onClick={() => toggle(it)}
                            className={"text-[10px] px-1.5 py-0.5 rounded-sm " + (it.is_published ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground")}
                            data-testid={`kp-toggle-${it.issue_number}`}>
                      {it.is_published ? "LIVE" : "DRAFT"}
                    </button>
                  </td>
                  <td className="py-2 pr-2 text-muted-foreground font-mono text-[11px] truncate max-w-[180px]">
                    {it.slug}
                  </td>
                  <td className="py-2 pr-2 text-right whitespace-nowrap">
                    <a href={`/karmpatra/${it.slug}`} target="_blank" rel="noopener noreferrer"
                       className="underline mr-2">view</a>
                    <button onClick={() => startEdit(it.issue_id)} className="underline mr-2"
                            data-testid={`kp-edit-${it.issue_number}`}>edit</button>
                    <button onClick={() => del(it.issue_id, it.issue_number)}
                            className="text-destructive underline"
                            data-testid={`kp-delete-${it.issue_number}`}>del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}



export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("directory");

  if (!user || (user.role !== "admin" && user.role !== "recruiter")) {
    return (
      <div className="container py-20 max-w-xl text-center">
        <h1 className="display-2 mb-4">Recruiter / Admin only</h1>
        <p className="text-muted-foreground">Sign up with the Recruiter role or sign in as admin@karm.app.</p>
      </div>
    );
  }

  const isAdmin = user.role === "admin";

  return (
    <div className="container py-10">
      <p className="eyebrow mb-1">{isAdmin ? "Admin" : "Recruiter"}</p>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <h1 className="display-2">Control center.</h1>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button asChild data-testid="open-admin-console">
              <Link to="/admin/console"><Activity className="mr-2 h-4 w-4" />Open Console</Link>
            </Button>
            <Button asChild variant="outline" data-testid="open-admin-users">
              <Link to="/admin/users"><ShieldCheck className="mr-2 h-4 w-4" />Manage Users</Link>
            </Button>
            <Button asChild variant="outline" data-testid="open-admin-email">
              <Link to="/admin/email"><Mail className="mr-2 h-4 w-4" />Email Audit Trail</Link>
            </Button>
            <Button asChild variant="outline" data-testid="open-admin-locations">
              <Link to="/admin/locations"><Globe className="mr-2 h-4 w-4" />Location Filter</Link>
            </Button>
            <Button asChild variant="outline" data-testid="open-admin-dedup-log">
              <Link to="/admin/dedup-log"><Database className="mr-2 h-4 w-4" />Removed Companies Log</Link>
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-2 md:grid-cols-11 mb-6" data-testid="admin-tabs">
          {isAdmin && <TabsTrigger value="directory" data-testid="admin-tab-directory"><Database className="h-4 w-4 mr-1.5" />Master Directory</TabsTrigger>}
          {isAdmin && <TabsTrigger value="coverage" data-testid="admin-tab-coverage"><Globe className="h-4 w-4 mr-1.5" />Crawl Coverage</TabsTrigger>}
          <TabsTrigger value="citi" data-testid="admin-tab-citi"><Bot className="h-4 w-4 mr-1.5" />Citi (deep)</TabsTrigger>
          {isAdmin && <TabsTrigger value="crawlers" data-testid="admin-tab-crawlers"><ChevronRight className="h-4 w-4 mr-1.5" />All crawlers</TabsTrigger>}
          {isAdmin && <TabsTrigger value="market" data-testid="admin-tab-market"><Activity className="h-4 w-4 mr-1.5" />Market Data</TabsTrigger>}
          {isAdmin && <TabsTrigger value="fresh" data-testid="admin-tab-fresh"><Sparkles className="h-4 w-4 mr-1.5" />Fresh Jobs</TabsTrigger>}
          {isAdmin && <TabsTrigger value="content-studio" data-testid="admin-tab-content-studio"><Cpu className="h-4 w-4 mr-1.5" />Content Studio</TabsTrigger>}
          {isAdmin && <TabsTrigger value="internal-ads" data-testid="admin-tab-internal-ads"><Megaphone className="h-4 w-4 mr-1.5" />Internal Ads</TabsTrigger>}
          {isAdmin && <TabsTrigger value="karmpatra" data-testid="admin-tab-karmpatra"><ScrollText className="h-4 w-4 mr-1.5" />Karmpatra</TabsTrigger>}
          {isAdmin && <TabsTrigger value="book-of-work" data-testid="admin-tab-book-of-work"><ClipboardList className="h-4 w-4 mr-1.5" />Book of Work</TabsTrigger>}
          <TabsTrigger value="import" data-testid="admin-tab-import"><Plus className="h-4 w-4 mr-1.5" />Import / Manual</TabsTrigger>
        </TabsList>

        {isAdmin && <TabsContent value="directory"><MasterDirectory /></TabsContent>}
        {isAdmin && <TabsContent value="coverage"><CrawlCoverage /></TabsContent>}
        <TabsContent value="citi"><CitiCrawler /></TabsContent>
        {isAdmin && <TabsContent value="crawlers"><MultiCrawler /></TabsContent>}
        {isAdmin && <TabsContent value="market"><MarketDataFeed /></TabsContent>}
        {isAdmin && <TabsContent value="fresh"><FreshJobsTab /></TabsContent>}
        {isAdmin && <TabsContent value="content-studio"><ContentStudioOps /></TabsContent>}
        {isAdmin && <TabsContent value="internal-ads"><InternalAdsAdmin /></TabsContent>}
        {isAdmin && <TabsContent value="karmpatra"><KarmpatraAdmin /></TabsContent>}
        {isAdmin && <TabsContent value="book-of-work"><BookOfWork /></TabsContent>}
        <TabsContent value="import"><ImportTools /></TabsContent>
      </Tabs>
    </div>
  );
}
